import Image from 'next/image';
import { useRouter } from 'next/router';

import { Box, Button, Icon, Paragraph, Table, Text } from '@zillow/constellation-10';
import { IconCheckmarkFilled } from '@zillow/constellation-icons';

import { useGetListingDetails } from '../../../../../hooks/queries/properties';

import { PRODUCT_TYPE_NAME_TO_ID } from '../../../../../constants/FreemiumConstants';

import PremiumBadge from '../../../../../assets/premium-badge.png';

import {
  ACTIVATE_MULTIPLEX_TRIGGER_OBJECT,
  type ActivateMultiplexTriggerObject,
  emitStartActivateFreemiumBottomEvent,
  emitStartActivatePremiumBottomEvent,
} from '../../../../../analytics/events/payAndPublish';
import { useAuth } from '../../../../AuthProvider';
import LearnAboutPremiumButton from './LearnAboutPremiumButton';

type Props = {
  onPublishClick: (isPremium: boolean, multiplexTriggerObject?: ActivateMultiplexTriggerObject) => void;
};

const ProductFeatureCompareTable = ({ onPublishClick }: Props) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { user } = useAuth();

  const { data: listingDetails, isLoading } = useGetListingDetails(listingAlias);
  const csEventParams = { currentProperty: { listingAlias }, user: user ?? {} };
  const products = listingDetails?.freemium?.products ?? [];
  const freeProduct = products.find((p) => p.type === PRODUCT_TYPE_NAME_TO_ID.FREE);
  const premiumProduct = products.find((p) => p.type === PRODUCT_TYPE_NAME_TO_ID.PREMIUM);

  const freePrice = freeProduct?.price ?? 0;
  const premiumPrice = premiumProduct?.price ?? 0;

  const renderPremiumOption = () => (
    <Box
      css={{
        position: 'relative',
        paddingBottom: '44px',
        textAlign: 'left',
        height: '100%',
      }}
    >
      <Paragraph textStyle="body-bold" css={{ marginBottom: '400' }}>
        Get the most out of your listing
      </Paragraph>
      <Paragraph css={{ marginBottom: '400' }}>
        Get more exposure on your listing and gain access to deeper insights and analytics to help you price your rental
        and optimize your listing.
      </Paragraph>

      <div>
        <Box css={{ display: 'flex', alignItems: 'center', flexDirection: 'row', justifyContent: 'space-between' }}>
          <div>
            <Paragraph css={{ textStyle: 'heading-xs', letterSpacing: '0.05em' }}>{premiumPrice}</Paragraph>
            <Paragraph textStyle="body-sm" color="text.subtle">
              One-time payment
            </Paragraph>
          </div>
          <Image src={PremiumBadge} alt="premium badge" width={53} height={53} />
        </Box>
      </div>

      <Button
        fluid={true}
        tone="brand"
        emphasis="filled"
        disabled={isLoading}
        loading={isLoading}
        css={{ position: 'absolute', bottom: 0 }}
        onClick={() => {
          emitStartActivatePremiumBottomEvent(csEventParams);
          onPublishClick(true, ACTIVATE_MULTIPLEX_TRIGGER_OBJECT.PUBLISH_WITH_PREMIUM);
        }}
        data-testid="ProductFeatureCompareTable-PublishWithPremiumButton"
      >
        Publish with premium
      </Button>
    </Box>
  );

  const renderFreeOption = () => (
    <Box
      css={{
        position: 'relative',
        paddingBottom: '44px',
        textAlign: 'left',
        height: '100%',
      }}
    >
      <Paragraph textStyle="body-bold" css={{ marginBottom: '400' }}>
        Use our basic listing services
      </Paragraph>
      <Paragraph css={{ marginBottom: '400' }}>
        List your property where millions of renters search each month and access our suite of landlord tools to screen
        tenants, sign a lease, and collect rent online.
      </Paragraph>

      <Paragraph css={{ textStyle: 'heading-xs' }}>{freePrice}</Paragraph>

      <Button
        fluid={true}
        tone="brand"
        emphasis="outlined"
        disabled={isLoading}
        loading={isLoading}
        css={{ position: 'absolute', bottom: 0 }}
        onClick={() => {
          emitStartActivateFreemiumBottomEvent(csEventParams);
          onPublishClick(false, ACTIVATE_MULTIPLEX_TRIGGER_OBJECT.PUBLISH_WITHOUT_PREMIUM);
        }}
        data-testid="ProductFeatureCompareTable-PublishWithoutPremiumButton"
      >
        No thanks, publish without premium
      </Button>
    </Box>
  );

  return (
    <Table.Root
      appearance="horizontal"
      size="md"
      alignments={['left', 'center', 'center']}
      aria-label="Compare premium and basic listing features"
      data-testid="ProductFeatureCompareTable"
    >
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell css={{ width: 'inherit', lg: { width: '348px' }, xxl: { width: '370px' } }}>
            {null}
          </Table.HeaderCell>
          <Table.HeaderCell
            css={{
              // oxlint-disable-next-line constellation/prefer-semantic-tokens -- ESLint's pandaLint set this warn-only; oxlint's oxlintPandaLint sets it error, no code change
              backgroundColor: 'blue.100',
              width: 'inherit',
              lg: { width: '348px' },
              xxl: { width: '370px' },
            }}
          >
            <Text css={{ textStyle: { base: 'body-xs-bold', xl: 'heading-xs' } }}>Premium</Text>
          </Table.HeaderCell>
          <Table.HeaderCell css={{ width: 'inherit', lg: { width: '348px' }, xxl: { width: '370px' } }}>
            <Text css={{ textStyle: { base: 'body-xs-bold', xl: 'heading-xs' } }}>Basic</Text>
          </Table.HeaderCell>
        </Table.Row>
      </Table.Header>

      <Table.Body>
        {[
          'Syndication to Trulia, HotPads, and Zillow',
          'Applications',
          'Online lease signing',
          'Online payments',
          'App / messaging platform',
        ].map((feature) => (
          <Table.Row key={feature}>
            <Table.Cell css={{ paddingLeft: '0' }}>
              <Text css={{ textStyle: { base: 'body-sm', xl: 'body-bold' } }}>{feature}</Text>
            </Table.Cell>
            <Table.Cell
              css={{
                // oxlint-disable-next-line constellation/prefer-semantic-tokens -- ESLint's pandaLint set this warn-only; oxlint's oxlintPandaLint sets it error, no code change
                backgroundColor: 'blue.100',
              }}
            >
              <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
            </Table.Cell>
            <Table.Cell>
              <Icon size="md" css={{ color: 'icon.action.neutral.default' }} render={<IconCheckmarkFilled />} />
            </Table.Cell>
          </Table.Row>
        ))}

        {['More exposure', 'Listing recommendations', 'Pricing & market insights', 'Priority support'].map(
          (feature) => (
            <Table.Row key={feature}>
              <Table.Cell css={{ paddingLeft: '0' }}>
                <Text css={{ textStyle: { base: 'body-sm', xl: 'body-bold' } }}>{feature}</Text>
              </Table.Cell>
              <Table.Cell
                css={{
                  // oxlint-disable-next-line constellation/prefer-semantic-tokens -- ESLint's pandaLint set this warn-only; oxlint's oxlintPandaLint sets it error, no code change
                  backgroundColor: 'blue.100',
                }}
              >
                <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
              </Table.Cell>
              <Table.Cell>{null}</Table.Cell>
            </Table.Row>
          ),
        )}

        <Box css={{ display: 'none', xxl: { display: 'contents' } }}>
          <Table.Row>
            <Table.Cell css={{ borderBottom: '0', paddingLeft: '0' }}>
              <LearnAboutPremiumButton as="TextButton">Learn more about premium</LearnAboutPremiumButton>
            </Table.Cell>
            <Table.Cell
              css={{
                borderBottom: '0',
                // oxlint-disable-next-line constellation/prefer-semantic-tokens -- ESLint's pandaLint set this warn-only; oxlint's oxlintPandaLint sets it error, no code change
                backgroundColor: 'blue.100',
                height: '270px',
              }}
            >
              {renderPremiumOption()}
            </Table.Cell>
            <Table.Cell css={{ borderBottom: '0', height: '270px' }}>{renderFreeOption()}</Table.Cell>
          </Table.Row>
        </Box>
      </Table.Body>
    </Table.Root>
  );
};

export default ProductFeatureCompareTable;
